import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, shareReplay, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface WdoOption {
  id: number;
  label: string;
}

export interface WdoConfig {
  mode: string;
  is_eleven_mode: boolean;
  type_range: string;
  sections: WdoOption[];
  locations: WdoOption[];
}

/**
 * The WDO vocabulary and the deployment's finding type mode.
 *
 * The five report sections and the eleven locations are fixed by the California
 * Structural Pest Control Board, so they are served from config/wdo.php rather than
 * duplicated here — a copy in Angular would drift from what the report actually uses.
 *
 * The response is cached for the life of the page: the mode is a deployment setting
 * and cannot change while somebody is looking at a screen.
 */
@Injectable({ providedIn: 'root' })
export class WdoService {
  private config$: Observable<WdoConfig>;

  constructor(private http: HttpClient) {}

  getConfig(): Observable<WdoConfig> {
    if (!this.config$) {
      this.config$ = this.http.get(`${environment.api}wdo/config`).pipe(
        map((resp: any) => this.unwrap(resp)),
        // A failed lookup must not blank out a Type field hint. Falling back to 1-5
        // matches the server, which treats anything not explicitly '1-11' as the
        // untouched path. Callers can tell a fallback from a real answer by checking
        // `sections` - see `loaded` below.
        catchError((err) => {
          console.error('wdo/config failed; falling back to 1-5 defaults', err);
          return of(this.fallback());
        }),
        shareReplay(1),
      );
    }
    return this.config$;
  }

  /**
   * This API has two response envelopes in circulation - `{status, result:{data}}` used by
   * the codes and revenue-type screens, and `{message, errors, data}` from the
   * response()->success() macro. Reading only the first is what silently left the WDO
   * Section dropdown showing nothing but "Not classified", so accept either.
   */
  private unwrap(resp: any): WdoConfig {
    const payload = (resp && resp.result && resp.result.data) ? resp.result.data
                  : (resp && resp.data) ? resp.data
                  : null;

    if (!payload || !payload.sections || !payload.sections.length) {
      console.error('wdo/config returned no sections; the WDO Section dropdown will be empty', resp);
      return this.fallback();
    }
    return payload;
  }

  /** True once the five sections have actually been loaded from the server. */
  loaded(cfg: WdoConfig): boolean {
    return !!cfg && !!cfg.sections && cfg.sections.length > 0;
  }

  /** Codes with no WDO section set. Non-empty in 1-11 mode means reports are not yet safe. */
  getUnclassifiedCodes(): Observable<any> {
    return this.http.get(`${environment.api}wdo/unclassified-codes`);
  }

  /** Pull the payload out of whichever envelope the endpoint used. */
  static payloadOf(resp: any): any {
    if (resp && resp.result && resp.result.data) { return resp.result.data; }
    if (resp && resp.data) { return resp.data; }
    return null;
  }

  private fallback(): WdoConfig {
    return {
      mode: '1-5',
      is_eleven_mode: false,
      type_range: '(1-5)',
      sections: [],
      locations: [],
    };
  }
}
